import { database } from './config';
import { ref, get } from 'firebase/database';
import { getAllEloRatings } from './eloManagement';

/**
 * Get all player names from the ACT history
 * @returns {Promise<string[]>} Array of unique player names
 */
export async function getAllPlayerNames() {
  try {
    const historyRef = ref(database, 'act-history');
    const snapshot = await get(historyRef);
    
    const playerNames = new Set();
    
    if (snapshot.exists()) {
      const data = snapshot.val();
      Object.values(data).forEach(entry => {
        if (entry.teams) {
          Object.values(entry.teams).forEach(team => {
            if (team.players) {
              Object.values(team.players).forEach(playerName => {
                if (playerName && playerName !== '' && playerName !== 'Player A' && playerName !== 'Player B') {
                  playerNames.add(playerName);
                }
              });
            }
          });
        }
      });
    }
    
    return Array.from(playerNames).sort();
  } catch (error) {
    console.error('Error fetching player names:', error);
    return [];
  }
}

/**
 * Get ELO rating for a specific player by name
 * First tries to find the player in the ELO database by name
 * @param {string} playerName - Player's name
 * @returns {Promise<number>} Player's ELO rating or 500 if not found
 */
export async function getPlayerEloByName(playerName) {
  try {
    if (!playerName) return 500;
    
    // Get all ELO ratings using history-based system
    const eloData = await getAllEloRatings();
      
    // Look for exact name match in ELO data (history-based returns key-value pairs)
    for (const [key, value] of Object.entries(eloData)) {
      if (key === playerName && typeof value === 'number') {
        return value;
      }
    }
    
    // Return default ELO if player not found
    return 500;
  } catch (error) {
    console.error(`Error fetching ELO for player ${playerName}:`, error);
    return 500;
  }
}

/**
 * Get ELO ratings for multiple players
 * @param {string[]} playerNames - Array of player names
 * @returns {Promise<Object>} Object mapping player names to ELO ratings
 */
export async function getMultiplePlayerElos(playerNames) {
  try {
    const eloMap = {};
    
    // Fetch all ELO data once using history-based system
    const eloData = await getAllEloRatings();
    
    for (const playerName of playerNames) {
      if (!playerName) {
        eloMap[playerName] = 500;
        continue;
      }
      
      // Look for exact name match (history-based returns key-value pairs)
      let foundElo = 500;
      
      for (const [key, value] of Object.entries(eloData)) {
        if (key === playerName && typeof value === 'number') {
          foundElo = value;
          break;
        }
      }
      
      eloMap[playerName] = foundElo;
    }
    
    return eloMap;
  } catch (error) {
    console.error('Error fetching multiple player ELOs:', error);
    // Return default ELOs for all players
    const defaultMap = {};
    playerNames.forEach(name => {
      defaultMap[name] = 500;
    });
    return defaultMap;
  }
}